import React, { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { AdminPage } from 'components';
import { H3 } from 'design-system-web';
import { convertToUUID } from 'uuid-encoding';
import { createVisitorCustomFieldConfig } from 'graphql-queries';
import type {
  CreateVisitorCustomFieldConfigMutation,
  CreateVisitorCustomFieldConfigMutationVariables,
} from 'graphql-query-contracts';
import type { Channel } from 'lane-shared/types/ChannelType';
import type { UserType } from 'lane-shared/types/User';
import LeaveConfirmationPrompt from '../../components/LeaveConfirmationPrompt/LeaveConfirmationPrompt';
import { CustomFieldModal } from '../visitor-configuration/tabs/customFields/CustomFieldModal';
import {
  buildCreateFieldConfig,
  runCreateCustomFieldMutation,
  CUSTOM_FIELD_CREATED_SUCCESS_KEY,
  MAX_ENABLED_FIELDS_REACHED_KEY,
  MAX_TOTAL_FIELDS_REACHED_KEY,
  NO_ORGANIZATION_KEY,
} from '../visitor-configuration/tabs/customFields/customFieldsTabModelUtils';
import {
  CUSTOM_FIELD_ENABLED_PER_ORG_MAX,
  CUSTOM_FIELD_TOTAL_PER_ORG_MAX,
} from '../../customFieldLimits';
import type { CustomFieldFormData } from '../../feature/components/CustomFieldsSection/customField.types';
import { useNavigateBackToCustomFields } from '../useNavigateBackToCustomFields';
import { useVisitorCustomFieldConfigs } from '../../hooks/useVisitorCustomFieldConfigs';

export function CustomFieldCreatePage({
  channel,
}: {
  channel?: Channel;
  user?: UserType;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const navigateBack = useNavigateBackToCustomFields();
  const [hasUnsavedDraft, setHasUnsavedDraft] = useState(false);
  const organizationId = channel?._id ? convertToUUID(channel._id) : null;
  const {
    data: orgCustomFields,
    fetch: fetchOrgCustomFields,
    loading: fieldsLoading,
    called: fieldsCalled,
    error: fieldsError,
  } = useVisitorCustomFieldConfigs();

  useEffect(() => {
    if (organizationId) {
      fetchOrgCustomFields(organizationId);
    }
  }, [organizationId, fetchOrgCustomFields]);

  const [createField] = useMutation<
    CreateVisitorCustomFieldConfigMutation,
    CreateVisitorCustomFieldConfigMutationVariables
  >(createVisitorCustomFieldConfig, { errorPolicy: 'all' });

  const fieldsUnavailable = !fieldsCalled || fieldsLoading || Boolean(fieldsError);

  const handleSave = useCallback(
    async (formData: CustomFieldFormData): Promise<boolean | null> => {
      if (!organizationId) {
        window.Toast.show(t(NO_ORGANIZATION_KEY));

        return null;
      }

      if (fieldsUnavailable) {
        return null;
      }

      const enabledFieldCount = orgCustomFields.filter(f => f.isEnabled).length;
      const totalFieldCount = orgCustomFields.length;

      if (totalFieldCount >= CUSTOM_FIELD_TOTAL_PER_ORG_MAX) {
        window.Toast.show(t(MAX_TOTAL_FIELDS_REACHED_KEY, { maxTotal: CUSTOM_FIELD_TOTAL_PER_ORG_MAX }));

        return null;
      }

      if (enabledFieldCount >= CUSTOM_FIELD_ENABLED_PER_ORG_MAX) {
        window.Toast.show(t(MAX_ENABLED_FIELDS_REACHED_KEY, { maxEnabled: CUSTOM_FIELD_ENABLED_PER_ORG_MAX }));

        return null;
      }

      return runCreateCustomFieldMutation({
        createField,
        config: buildCreateFieldConfig(formData, organizationId),
        t,
        showToast: (msg: string) => window.Toast.show(msg),
        onSuccess: () => {
          flushSync(() => setHasUnsavedDraft(false));
          window.Toast.show(t(CUSTOM_FIELD_CREATED_SUCCESS_KEY));
        },
      });
    },
    [createField, fieldsUnavailable, organizationId, orgCustomFields, t]
  );

  return (
    <>
      <LeaveConfirmationPrompt
        when={hasUnsavedDraft}
        navigate={history.push}
        title={t('web.admin.visitorManagement.configuration.leavePrompt.title')}
        description={t('web.admin.visitorManagement.configuration.leavePrompt.description')}
      />
      <AdminPage>
        <H3 as="h1">{t('web.admin.visitorManagement.customField.create.heading')}</H3>
        <CustomFieldModal
          onClose={navigateBack}
          onSave={handleSave}
          onUnsavedDraftChange={setHasUnsavedDraft}
          saveDisabled={fieldsUnavailable}
        />
      </AdminPage>
    </>
  );
}
